import { clsx } from 'clsx';
import { ChevronRight, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { formatPercent } from '@kts/shared';

// ------------------------------------------------------------------ Avatar
export function Avatar({ name, src, size = 36, className }: { name: string; src?: string | null; size?: number; className?: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
  return (
    <span className={clsx('kts-avatar', className)} style={{ ['--size' as string]: `${size}px` }} aria-hidden>
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
}

// ------------------------------------------------------------------ Delta (o'zgarish)
/** O'zgarish ko'rsatkichi. `goodWhen` — o'sish yaxshimi (daromad) yoki kamayish (qaytarishlar). */
export function Delta({ value, period, goodWhen = 'up' }: { value: number | null | undefined; period?: string; goodWhen?: 'up' | 'down' }) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const dir = Math.abs(value) < 0.05 ? 'flat' : value > 0 ? 'up' : 'down';
  const good = dir === 'flat' ? undefined : (dir === 'up') === (goodWhen === 'up');
  const Icon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
  return (
    <span className="kts-delta" data-dir={dir} data-good={good === undefined ? undefined : String(good)}>
      <Icon />
      {formatPercent(value)}
      {period && <span className="kts-delta__period">{period}</span>}
    </span>
  );
}

// ------------------------------------------------------------------ Stat (KPI kartasi)
export function StatCard({
  label,
  value,
  unit,
  icon,
  delta,
  deltaPeriod = 'oldingi davrga',
  goodWhen,
  footer,
  trend,
  className,
  onClick,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  icon?: ReactNode;
  delta?: number | null;
  deltaPeriod?: string;
  goodWhen?: 'up' | 'down';
  footer?: ReactNode;
  trend?: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <section
      className={clsx('kts-card', onClick && 'kts-card--interactive', className)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="kts-stat">
        <div className="kts-stat__top">
          {icon && <div className="kts-stat__icon">{icon}</div>}
          <div className="kts-stat__label">{label}</div>
        </div>
        <div className="kts-stat__value">
          {value}
          {unit && <span className="kts-stat__unit">{unit}</span>}
        </div>
        <div className="kts-stat__foot">
          {delta !== undefined ? <Delta value={delta} period={deltaPeriod} goodWhen={goodWhen} /> : <span>{footer}</span>}
          {trend && <div style={{ width: 96, height: 28 }}>{trend}</div>}
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ Kalit-qiymat
export function KeyValue({ items, className }: { items: { label: ReactNode; value: ReactNode; hidden?: boolean }[]; className?: string }) {
  return (
    <dl className={clsx('kts-kv', className)}>
      {items
        .filter((i) => !i.hidden)
        .map((i, idx) => (
          <div key={idx} style={{ display: 'contents' }}>
            <dt>{i.label}</dt>
            <dd>{i.value ?? '—'}</dd>
          </div>
        ))}
    </dl>
  );
}

// ------------------------------------------------------------------ Timeline (status tarixi)
export function Timeline({ items }: { items: { key: string; title: ReactNode; meta?: ReactNode; body?: ReactNode }[] }) {
  return (
    <div className="kts-timeline">
      {items.map((it) => (
        <div key={it.key} className="kts-timeline__item">
          <span className="kts-timeline__dot" />
          <div>
            <div className="kts-timeline__title">{it.title}</div>
            {it.meta && <div className="kts-timeline__meta">{it.meta}</div>}
            {it.body && <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-2)' }}>{it.body}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ Sahifa sarlavhasi
export function PageHeader({
  title,
  description,
  actions,
  crumbs,
  renderLink,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  crumbs?: { label: ReactNode; to?: string }[];
  /** Router havolasi (ui paketi routerga bog'lanmasin). */
  renderLink?: (to: string, children: ReactNode) => ReactNode;
}) {
  return (
    <div className="kts-page-header">
      <div className="kts-page-header__titles">
        {crumbs && crumbs.length > 0 && (
          <nav className="kts-page-header__crumbs" aria-label="Yo‘l">
            {crumbs.map((c, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {c.to && renderLink ? renderLink(c.to, c.label) : <span>{c.label}</span>}
                {i < crumbs.length - 1 && <ChevronRight size={13} />}
              </span>
            ))}
          </nav>
        )}
        <h1>{title}</h1>
        {description && <div className="kts-page-header__desc">{description}</div>}
      </div>
      {actions && <div className="kts-page-header__actions">{actions}</div>}
    </div>
  );
}

// ------------------------------------------------------------------ Logo
/**
 * Keytosell belgisi — brend asari (`public/logo-mark.png`, `brand/` papkasidan tayyorlangan).
 * Rasm qorong'i yumaloq kvadrat fon bilan keladi, shuning uchun ikkala temada ham bir xil ko'rinadi.
 * Boshqa manzil kerak bo'lsa (masalan, boshqa bazaviy yo'l) — `src` bilan beriladi.
 */
export function LogoMark({ size = 32, src = '/logo-mark.png' }: { size?: number; src?: string }) {
  return (
    <img
      src={src}
      width={size}
      height={size}
      alt=""
      aria-hidden
      draggable={false}
      style={{ width: size, height: size, borderRadius: size * 0.3, display: 'block', flexShrink: 0 }}
    />
  );
}

export function Logo({ size = 32, withText = true, sub }: { size?: number; withText?: boolean; sub?: ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <LogoMark size={size} />
      {withText && (
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
          <span className="kts-sidebar__brand-name">Keytosell</span>
          {sub && <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>{sub}</span>}
        </span>
      )}
    </span>
  );
}
